import { cva } from '@/styled-system/css';

export const buttonStyles = cva({
  base: {
    margin: 0,
    fontSize: 'sm',
    lineHeight: '16px',
    border: 'none',
    borderRadius: '4px',
    boxShadow: 'none',
    cursor: 'pointer',
    letterSpacing: '0.5px',
    fontWeight: 'normal',
    userSelect: 'none',
    display: 'table-cell',
    verticalAlign: 'middle',
    transitionProperty: 'background-color, border-color, color',
    transitionDuration: '0.15s',
    transitionTimingFunction: 'ease-in-out',

    '&:hover, &:active': {
      outline: 'none',
    },
  },
  variants: {
    size: {
      xs: {
        fontSize: 'tiny',
        height: '24px',
        paddingLeft: '2x',
        paddingRight: '2x',
      },
      sm: {
        fontSize: 'sm',
        height: '32px',
        paddingLeft: '2x',
        paddingRight: '2x',
      },
      md: {
        fontSize: 'md',
        height: '40px',
        paddingLeft: '3x',
        paddingRight: '3x',
      },
      lg: {
        fontSize: 'md',
        height: '48px',
        paddingLeft: '4x',
        paddingRight: '4x',
      },
    },
    btnType: {
      primary: {
        color: 'hpxWhite',
        fontWeight: 'bold',
        backgroundColor: 'hpxTeal500',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'hpxTeal500',

        '&:focus': {
          borderColor: 'hpxTeal600',
          backgroundColor: 'hpxTeal600',
        },

        '@media (hover: hover)': {
          '&:hover': {
            borderColor: 'hpxTeal600',
            backgroundColor: 'hpxTeal600',
          },
        },

        '&.active, &:active': {
          borderColor: 'hpxTeal700',
          backgroundColor: 'hpxTeal700',
        },
      },
      'primary-outline': {
        color: 'hpxTeal500',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'hpxTeal500',
        backgroundColor: 'hpxWhite',

        '&:focus': {
          backgroundColor: 'hpxGrey100',
        },

        '@media (hover: hover)': {
          '&:hover': {
            backgroundColor: 'hpxGrey100',
          },
        },

        '&.active, &:active': {
          color: 'hpxWhite',
          backgroundColor: 'hpxTeal500',
        },
      },
      secondary: {
        color: 'hpxGrey600',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'hpxGrey500',
        backgroundColor: 'hpxWhite',

        '&:focus': {
          borderColor: 'hpxGrey500',
          backgroundColor: 'hpxGrey100',
        },

        '@media (hover: hover)': {
          '&:hover': {
            borderColor: 'hpxGrey500',
            backgroundColor: 'hpxGrey100',
          },
        },

        '&.active, &:active': {
          color: 'hpxWhite',
          borderColor: 'hpxBlue500',
          backgroundColor: 'hpxBlue500',
        },
      },
      tertiary: {
        color: 'hpxWhite',
        fontWeight: 'bold',
        backgroundColor: 'hpPurple500',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'hpPurple500',

        '&:focus': {
          borderColor: 'hpPurple600',
          backgroundColor: 'hpPurple600',
        },

        '@media (hover: hover)': {
          '&:hover': {
            borderColor: 'hpPurple600',
            backgroundColor: 'hpPurple600',
          },
        },

        '&.active, &:active': {
          borderColor: 'hpPurple700',
          backgroundColor: 'hpPurple700',
        },
      },
      'text-color': {
        color: 'hpxWhite',
        backgroundColor: 'hpxBlue500',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'hpxBlue500',
        fontWeight: 'bold',

        '&:focus': {
          borderColor: 'hpxBlue600',
          backgroundColor: 'hpxBlue600',
        },

        '@media (hover: hover)': {
          '&:hover': {
            borderColor: 'hpxBlue600',
            backgroundColor: 'hpxBlue600',
          },
        },

        '&.active, &:active': {
          borderColor: 'hpxBlue700',
          backgroundColor: 'hpxBlue700',
        },
      },
      'text-color-outline': {
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'hpxBlue500',
        backgroundColor: 'hpxWhite',
        color: 'hpxBlue500',
        fontWeight: 'bold',

        '&:focus': {
          backgroundColor: 'hpxGrey100',
        },

        '@media (hover: hover)': {
          '&:hover': {
            backgroundColor: 'hpxGrey100',
          },
        },

        '&.active, &:active': {
          color: 'hpxWhite',
          backgroundColor: 'hpxBlue500',
        },
      },
      'text-color-light': {
        color: 'hpxWhite',
        backgroundColor: 'hpxBlue600',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'hpxWhite',
        fontWeight: 'normal',

        '&:focus': {
          backgroundColor: 'hpxBlue500',
        },

        '@media (hover: hover)': {
          '&:hover': {
            backgroundColor: 'hpxBlue500',
          },
        },

        '&.active, &:active': {
          backgroundColor: 'hpxBlue700',
        },
      },
      default: {
        color: 'hpxBlue600',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'hpxGrey500',
        backgroundColor: 'hpxWhite',

        '&:focus': {
          backgroundColor: 'hpxTeal100',
        },

        '@media (hover: hover)': {
          '&:hover': {
            backgroundColor: 'hpxTeal100',
          },
        },

        '&.active, &:active': {
          color: 'hpxWhite',
          borderColor: 'hpxTeal700',
          backgroundColor: 'hpxTeal700',
        },
      },
      warning: {
        color: 'hpxRed500',
        backgroundColor: 'hpxWhite',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'hpxGrey500',

        '&:focus': {
          color: 'hpxWhite',
          borderColor: 'hpxRed500',
          backgroundColor: 'hpxRed500',
        },

        '@media (hover: hover)': {
          '&:hover': {
            color: 'hpxWhite',
            borderColor: 'hpxRed500',
            backgroundColor: 'hpxRed500',
          },
        },

        '&.active, &:active': {
          color: 'hpxWhite',
          borderColor: 'hpxRed600',
          backgroundColor: 'hpxRed600',
        },
      },
      'alert-secondary': {
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'hpxRed500',
        color: 'hpxGrey600',
        backgroundColor: 'transparent',

        '@media (hover: hover)': {
          '&:hover': {
            outlineWidth: '0',
            outlineStyle: 'none',
            borderColor: 'hpxRed600',
          },
        },
      },
      'primary-inactive': {
        cursor: 'not-allowed',
        pointerEvents: 'none',
        color: 'hpxWhite',
        fontWeight: 'bold',
        backgroundColor: 'hpxTeal600',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'hpxTeal600',
      },
      dark: {
        color: 'hpxWhite',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'hpxWhite',
        backgroundColor: 'hpxBlack',

        '&:focus': {
          color: 'hpxGrey100',
          borderColor: 'hpxGrey100',
        },

        '@media (hover: hover)': {
          '&:hover': {
            color: 'hpxGrey100',
            borderColor: 'hpxGrey100',
          },
        },
      },
      link: {
        color: 'hpxGrey600',
        backgroundColor: 'transparent',
        border: 'none',
        textDecoration: 'underline',
        padding: 0,
        height: 'auto',

        '&:focus': {
          outlineWidth: '2px',
          outlineStyle: 'solid',
          outlineColor: 'hpxTeal500',
          outlineOffset: '2px',
        },

        '@media (hover: hover)': {
          '&:hover': {
            color: 'hpxBlue600',
          },
        },

        '&.active, &:active': {
          color: 'hpxBlue700',
        },
      },
      text: {
        color: 'hpxGrey600',
        backgroundColor: 'transparent',
        border: 'none',
        fontWeight: 'bold',
        padding: 0,
        height: 'auto',

        '&:focus': {
          outlineWidth: '2px',
          outlineStyle: 'solid',
          outlineColor: 'hpxTeal500',
          outlineOffset: '2px',
        },

        '@media (hover: hover)': {
          '&:hover': {
            color: 'hpxBlue600',
          },
        },

        '&.active, &:active': {
          color: 'hpxBlue700',
        },
      },
    },
    full: {
      true: {
        width: '100%',
      },
    },
    bold: {
      true: {
        fontWeight: 'bold',
      },
    },
    disabled: {
      true: {
        fontWeight: 'normal !important',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'hpxGrey500 !important',
        backgroundColor: 'hpxGrey100 !important',
        color: 'hpxGrey600 !important',
        pointerEvents: 'none',
        cursor: 'not-allowed',
      },
    },
    rounded: {
      true: {},
    },
    inputButtonGroupPosition: {
      before: {
        borderBottomRightRadius: '0px',
        borderTopRightRadius: '0px',
      },
      after: {
        borderBottomLeftRadius: '0px',
        borderTopLeftRadius: '0px',
        marginLeft: '-1px',
      },
    },
    hideInputBorder: {
      true: {},
    },
    active: {
      true: {
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'hpxTeal600 !important',
        color: 'hpxTeal600 !important',
        fontWeight: 'bold !important',
      },
    },
  },
  compoundVariants: [
    {
      rounded: true,
      size: 'xs',
      css: {
        borderRadius: '16px',
      },
    },
    {
      rounded: true,
      size: 'sm',
      css: {
        borderRadius: '16px',
      },
    },
    {
      rounded: true,
      size: 'md',
      css: {
        borderRadius: '20px',
      },
    },
    {
      rounded: true,
      size: 'lg',
      css: {
        borderRadius: '24px',
      },
    },
    // InputButtonGroup + hideInputBorder combinations
    {
      inputButtonGroupPosition: 'before',
      hideInputBorder: true,
      css: {
        borderRightColor: 'hpxWhite',
      },
    },
    {
      inputButtonGroupPosition: 'after',
      hideInputBorder: true,
      css: {
        borderLeftColor: 'hpxWhite',
      },
    },
  ],
});

// Container styles using CVA for iconGap functionality
export const buttonContentContainerStyles = cva({
  base: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  variants: {
    iconGap: {
      none: {
        gap: '0px',
      },
      xs: {
        gap: 'xs',
      },
      sm: {
        gap: '1x',
      },
      '': {
        gap: '1x',
      },
    },
    showCarat: {
      true: {
        justifyContent: 'space-between',
        width: '100%',
      },
      false: {},
    },
  },
});

// Carat styles with rotation
export const caratStyles = cva({
  base: {
    display: 'inline-block',
    transitionProperty: 'transform',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease-in-out',
    transform: 'rotate(-90deg)',
    flexShrink: 0,
  },
  variants: {
    expanded: {
      true: {
        transform: 'rotate(90deg)',
      },
      false: {
        transform: 'rotate(-90deg)',
      },
    },
  },
});
